import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  clearAnswer,
  completeAssignment,
  getAssignmentReader,
  getCurateStatus,
  getMyAssignments,
  openAssignment,
  saveAnswer,
  saveProgress,
  type MyAssignmentsFilters,
  type SaveAnswerBody,
  type SaveProgressBody,
} from '@/api/curate';
import { ApiError } from '@/api/client';
import { queryKeys } from './queryKeys';

/**
 * React Query bindings for the Curate learner surface.
 *
 * Two conventions worth knowing before adding to this file:
 *
 * 1. `useCurateStatus` gates the rest. Every other Curate route answers
 *    `503 CURATE_NOT_ENABLED` when the tenant has no Curate tables, which is a
 *    normal state rather than an error, so the assignments query stays disabled
 *    until the probe says the feature is on. That keeps a disabled tenant from
 *    generating a stream of failed requests and retry noise.
 *
 * 2. Progress and answer mutations invalidate the reader query and the
 *    assignment list, because both carry derived state the server owns —
 *    `progressPct` on the tiles, `state`/`score` on the assignment. Recomputing
 *    those on the client would risk drifting from the server's own derivation.
 */

/** Retries are pointless for these: they are policy answers, not blips. */
const isTerminalCurateError = (error: unknown): boolean =>
  error instanceof ApiError && (error.status === 503 || error.status === 403);

export const useCurateStatus = () =>
  useQuery({
    queryKey: queryKeys.curateStatus(),
    queryFn: ({ signal }) => getCurateStatus(signal),
    // The server caches its own probe for ~60s; matching that here avoids
    // re-asking on every navigation back to the page.
    staleTime: 60_000,
  });

/**
 * The signed-in provider's assignments.
 *
 * `enabled` is false until the availability probe succeeds AND reports the
 * feature on, so a tenant without Curate tables never issues this request.
 */
export const useMyAssignments = (filters?: MyAssignmentsFilters, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: queryKeys.curateAssignments(filters),
    queryFn: ({ signal }) => getMyAssignments(filters, signal),
    enabled: options?.enabled ?? true,
    retry: (failureCount, error) => !isTerminalCurateError(error) && failureCount < 2,
  });

/** Full reader payload for one assignment. Disabled until an id is supplied. */
export const useAssignmentReader = (assignmentId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.curateReader(assignmentId ?? ''),
    queryFn: ({ signal }) => getAssignmentReader(assignmentId as string, signal),
    enabled: !!assignmentId,
    retry: (failureCount, error) => !isTerminalCurateError(error) && failureCount < 2,
  });

/**
 * Record an open. Fired once when the reader mounts.
 *
 * Deliberately does NOT invalidate the reader query: the reader has just
 * fetched, and refetching it here would race the render and reset the page the
 * learner was resumed onto. The assignment list is invalidated instead, so the
 * tile behind the modal reflects the `new` -> `in_progress` transition.
 */
export const useOpenAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) => openAssignment(assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kai', 'curate', 'assignments'] });
    },
  });
};

/**
 * Save reading position.
 *
 * Failures are swallowed by callers on purpose: losing a page-position write is
 * an annoyance (the learner resumes a page early next time), never a reason to
 * interrupt a reading session with an error. The close write in particular is
 * best-effort, since the modal is already unmounting.
 */
export const useSaveProgress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, ...body }: SaveProgressBody & { assignmentId: string }) =>
      saveProgress(assignmentId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kai', 'curate', 'assignments'] });
    },
  });
};

/**
 * Upsert one answer. Grading is server-side, so the saved response coming back
 * is the only source of truth for correctness — the caller renders feedback
 * from the mutation result, never from the option payload.
 */
export const useSaveAnswer = (assignmentId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ blockId, ...body }: SaveAnswerBody & { blockId: string }) =>
      saveAnswer(assignmentId as string, blockId, body),
    onSuccess: () => {
      if (assignmentId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.curateReader(assignmentId) });
      }
    },
  });
};

export const useClearAnswer = (assignmentId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (blockId: string) => clearAnswer(assignmentId as string, blockId),
    onSuccess: () => {
      if (assignmentId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.curateReader(assignmentId) });
      }
    },
  });
};

/** Mark complete. Invalidates both the reader and the tile list. */
export const useCompleteAssignment = (assignmentId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => completeAssignment(assignmentId as string),
    onSuccess: () => {
      if (assignmentId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.curateReader(assignmentId) });
      }
      queryClient.invalidateQueries({ queryKey: ['kai', 'curate', 'assignments'] });
    },
  });
};
